<template>
  <div>
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              <div>
                Search Results
              </div>
            </div>
          </template>
          <template #actions
            v-if="sharedIdResults
              && sharedIdResults.length
              && !loading
              && !loadingError
              && hasModule('PRSID:IDENT:DOWNLOAD')"
          >
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <a :href="downloadSharedIdRecords()">
                    <font-awesome-icon
                      :icon="['far', 'file-download']"
                      class="fa-2x"
                      title="Download Records"
                    />
                      </a>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div>
        <es-loader v-if="loading" style="margin: 6.5rem"/>
        <es-exception v-else-if="loadingError"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="There was an issue. Please contact the admin"
        />
        <es-exception
          v-else-if="!sharedIdResults"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="Search criteria must be entered to view results."
        />
        <es-exception
          v-else-if="!sharedIdResults.length"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="No results were found."
        />
        <div class="table-container" v-else style="padding: 2rem 3rem">
          <table class="table is-fullwidth is-hoverable">
            <es-sorter
              :columnHeaders="columnHeaders"
            />
            <tbody>
              <tr v-for="(record, index) in sharedIdResults" :key="index">
                <td>
                  <div class="badge" style="background: #5953A4">
                    M
                  </div>
                </td>
                <td>
                  {{ record.stateStudentId }}
                </td>
                <td class="weighted-font">
                  {{ record.lastName }}
                </td>
                <td class="weighted-font">
                  {{ record.middleName ? record.middleName : null }}
                </td>
                <td class="weighted-font">
                  {{ record.firstName }}
                </td>
                <td class="weighted-font">
                  {{ record.dateOfBirth | formatDate }}
                </td>
                <td>
                  {{ mapGender(record.genderCode) }}
                </td>
                <td style="text-align: right;">
                  <router-link
                    :to="{
                      name: 'PISplitIdDetails',
                      query: {
                        stateId: record.stateStudentId,
                        shareId: true
                      },
                    }"
                  >
                    <font-awesome-icon
                      :icon="['far', 'angle-double-right']"
                      size="2x"
                      title="View Details"
                    />
                  </router-link>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="!loading && this.sharedIdResults !== null">
              <tr>
                <td colspan="100%">
                  <es-pager :totalCount="totalCount"/>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faFileDownload,
} from '@fortawesome/pro-regular-svg-icons';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
  faFileDownload,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
  },
  props: {
    loading: {
      type: Boolean,
    },
    loadingError: {
      type: Boolean,
    },
    personType: {
      type: String,
    },
    sharedIdResults: {
      type: Array,
    },
    totalCount: {
      type: Number,
    },
  },
  data() {
    return {
      fileNameForDownload: null,
    };
  },
  computed: {
    ...mapGetters('lookups', [
      'activeGenders',
      'defaultPersonType',
    ]),
    columnHeaders() {
      const temp = [
        {
          header: null,
          sortable: false,
          order: null,
        },
        {
          header: 'State ID',
          field: 'stateStudentId',
          sortable: false,
          order: 'ASC',
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType ? this.personType : this.defaultPersonType}:ASSIGN:LAST_NAME`)}`,
          field: 'lastname',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType ? this.personType : this.defaultPersonType}:ASSIGN:MIDDLE_NAME`)}`,
          field: 'middlename',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType ? this.personType : this.defaultPersonType}:ASSIGN:FIRST_NAME`)}`,
          field: 'firstname',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType ? this.personType : this.defaultPersonType}:ASSIGN:DATE_OF_BIRTH`)}`,
          field: 'dob',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType ? this.personType : this.defaultPersonType}:ASSIGN:GENDER_CODE`)}`,
          field: 'gender',
          sortable: false,
          order: null,
        },
        {
          header: 'Actions',
          field: 'actions',
          sortable: false,
          order: null,
          style: 'text-align: right; width: 1%; white-space: nowrap;',
        },
      ];
      return temp;
    },
  },
  methods: {
    downloadSharedIdRecords() {
      let url = `${axios.defaults.baseURL}idmanager/download?type=sharedIdSearch`;
      if (this.personType) {
        url += `&personType=${this.personType}`;
      }
      if (this.$route.query.fields) {
        url += `&fields=${this.$route.query.fields}`;
      }
      return url;
    },
    mapGender(genderCode) {
      return this.activeGenders.find(gender => gender.code === genderCode).description;
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    max-width: 100%
    white-space: nowrap
  table td
    max-width: 100%
    white-space: nowrap
  .badge
    min-width: 25px
    border-radius: 50%
    width: 35px
    height: 35px
    padding: 6px
    color: #fff
    text-align: center
    font-size: 1.25rem
    font-weight: 700
  .weighted-font
    font-weight: 700 !important
  .table-container
    overflow-x: auto
</style>
